import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import LanguageSelector from '../../components/LanguageSelector';
import { EyeIcon, EyeSlashIcon, ShieldCheckIcon, LockClosedIcon } from '@heroicons/react/24/outline';

const AdminLogin = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { login, loading, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const { register, handleSubmit, formState: { errors } } = useForm();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/admin';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const onSubmit = async (data) => {
    setSubmitting(true);
    const result = await login(data);
    setSubmitting(false);
    if (result.success) {
      const from = location.state?.from?.pathname || '/admin';
      navigate(from, { replace: true });
    }
  };

  if (loading && !submitting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F7FA]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF6B00]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side — branding panel */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1578575437130-527eed3abbec?auto=format&fit=crop&w=1200&q=80"
            alt="Container ship at port"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1F3A]/95 via-[#123B63]/90 to-[#0B1F3A]/95"></div>
        </div>
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <Link to="/" className="flex items-center space-x-3 group">
            <img
              src="/logo-mark.svg"
              alt="DutchCargoExpress"
              className="h-12 w-12 transition-transform group-hover:scale-105"
            />
            <div className="leading-none">
              <span className="text-xl font-extrabold block tracking-tight">
                DutchCargo<span className="text-[#FF6B00]">Express</span>
              </span>
              <span className="text-xs text-gray-300 block mt-1 tracking-wide uppercase">{t.nav.globalLogistics}</span>
            </div>
          </Link>

          <div>
            <h2 className="text-3xl font-bold mb-4 leading-tight">
              {t.auth.adminPortal}
            </h2>
            <p className="text-gray-300 text-lg leading-relaxed max-w-md">
              {t.auth.portalDesc}
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/10 px-4 py-2 rounded-lg">
                <ShieldCheckIcon className="h-5 w-5 text-[#FF6B00]" />
                <span className="text-sm">{t.auth.secureAccess}</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/10 px-4 py-2 rounded-lg">
                <LockClosedIcon className="h-5 w-5 text-[#FF6B00]" />
                <span className="text-sm">{t.auth.encryption}</span>
              </div>
            </div>
          </div>

          <div className="text-gray-400 text-sm">
            {t.auth.rightsReserved}
          </div>
        </div>
      </div>

      {/* Right side — login form */}
      <div className="flex-1 flex flex-col justify-center bg-[#F5F7FA] px-4 sm:px-6 lg:px-8 py-12">
        <div className="mx-auto w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex justify-center lg:hidden">
            <Link to="/" className="flex items-center space-x-3 group">
              <img
                src="/logo-mark.svg"
                alt="DutchCargoExpress"
                className="h-12 w-12"
              />
              <div className="leading-none">
                <span className="text-xl font-extrabold text-[#0B1F3A] block tracking-tight">
                  DutchCargo<span className="text-[#FF6B00]">Express</span>
                </span>
                <span className="text-xs text-gray-500 block mt-1 tracking-wide uppercase">{t.nav.globalLogistics}</span>
              </div>
            </Link>
          </div>

          {/* Language Selector */}
          <div className="flex justify-center lg:justify-end mb-4">
            <LanguageSelector />
          </div>

          <div className="mt-4 lg:mt-0 text-center lg:text-left">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-[#0B1F3A] rounded-full mb-4 shadow-md">
              <LockClosedIcon className="h-7 w-7 text-[#FF6B00]" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0B1F3A] mb-2">
              {t.auth.adminLogin}
            </h2>
            <p className="text-[#64748B] text-sm mb-8">
              {t.auth.loginSubtitle}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 md:p-8">
            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t.auth.email}
                </label>
                <input
                  id="username"
                  type="text"
                  autoComplete="email"
                  maxLength={255}
                  {...register('username', { required: t.auth.emailRequired, maxLength: 255 })}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 text-sm ${errors.username ? 'border-[#DC2626]' : 'border-[#E2E8F0] focus:border-[#FF6B00]'}`}
                  placeholder={t.auth.emailPlaceholder}
                />
                {errors.username && (
                  <p className="mt-1 text-sm text-red-600">{errors.username.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t.auth.password}
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    maxLength={255}
                    {...register('password', { required: t.auth.passwordRequired, maxLength: 255 })}
                    className={`w-full px-4 py-3 pr-12 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 text-sm ${errors.password ? 'border-[#DC2626]' : 'border-[#E2E8F0] focus:border-[#FF6B00]'}`}
                    placeholder={t.auth.passwordPlaceholder}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-4 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full px-6 py-3.5 bg-[#FF6B00] hover:bg-[#e65f00] text-white font-semibold rounded-lg transition-colors flex items-center justify-center space-x-2"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>{t.auth.signingIn}</span>
                  </>
                ) : (
                  <>
                    <LockClosedIcon className="h-5 w-5" />
                    <span>{t.auth.signIn}</span>
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-100 text-center">
              <Link
                to="/contact"
                className="text-[#FF6B00] hover:text-[#e65f00] text-sm font-medium transition-colors"
              >
                {t.auth.contactSupport}
              </Link>
            </div>
          </div>

          <div className="mt-6 text-center">
            <Link
              to="/"
              className="text-[#64748B] hover:text-[#FF6B00] text-sm font-medium transition-colors"
            >
              {t.auth.backHome}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
